import { useState, useEffect, useCallback } from "react";
import {
  LaboratoryData,
  LaboratoryReportData,
  GynAdequacySummaryData,
  GynAdequacyBySourceData,
  CytotechDiagnosisSummaryData,
  PathologistDiagnosisSummaryData,
  GynDiagnosisSummaryData,
  GynPapBxCorrelationData,
  TotalPapVolumeData,
  ScreeningVsFinalDiagnosisData,
  HSILRetrospectiveReviewData,
  NonGynCytologyDiagnosisCorrelationData,
  NonGynSourceCorrelationData,
  CytotechDiagnosisAndHpvData,
  AgeAndHpvData,
  HpvTotalThinprepData,
  DiagnosisAndHpvSummaryData,
} from "../types/MedicalData";
import { LaboratoryApiService, ApiError } from "../services/api";
import { exportData as exportDataUtil } from "../utils/exportUtils";

// Custom hook for managing laboratory data with loading states and error handling
export const useLaboratoryData = () => {
  const [data, setData] = useState<LaboratoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const metrics = await LaboratoryApiService.getLaboratoryMetrics();
      setData(metrics || []);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : "Failed to fetch laboratory data";
      setError(errorMessage);
      console.error("Error fetching laboratory data:", err);
      setData([]); // Ensure data is always an array
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};

// Hook for fetching a specific metric
export const useMetric = (metricId: string) => {
  const [data, setData] = useState<LaboratoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetric = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const metric = await LaboratoryApiService.getMetricById(metricId);
      setData(metric);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError ? err.message : "Failed to fetch metric data";
      setError(errorMessage);
      console.error(`Error fetching metric ${metricId}:`, err);
    } finally {
      setLoading(false);
    }
  }, [metricId]);

  useEffect(() => {
    if (metricId) {
      fetchMetric();
    }
  }, [metricId, fetchMetric]);

  return {
    data,
    loading,
    error,
    refetch: fetchMetric,
  };
};

// Hook for fetching detailed report data
export const useMetricReport = (metricId: string, month?: string) => {
  const [data, setData] = useState<
    | LaboratoryReportData
    | GynAdequacySummaryData
    | GynAdequacyBySourceData
    | CytotechDiagnosisSummaryData
    | PathologistDiagnosisSummaryData
    | GynDiagnosisSummaryData
    | GynPapBxCorrelationData
    | TotalPapVolumeData
    | ScreeningVsFinalDiagnosisData
    | HSILRetrospectiveReviewData
    | NonGynCytologyDiagnosisCorrelationData
    | NonGynSourceCorrelationData
    | CytotechDiagnosisAndHpvData
    | AgeAndHpvData
    | HpvTotalThinprepData
    | DiagnosisAndHpvSummaryData
    | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const report = await LaboratoryApiService.getMetricReport(
        metricId,
        month
      );
      setData(report);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError ? err.message : "Failed to fetch report data";
      setError(errorMessage);
      console.error(`Error fetching report for metric ${metricId}:`, err);
    } finally {
      setLoading(false);
    }
  }, [metricId, month]);

  useEffect(() => {
    if (metricId) {
      fetchReport();
    }
  }, [metricId, month, fetchReport]);

  return {
    data,
    loading,
    error,
    refetch: fetchReport,
  };
};

// Hook for fetching compliance status
export const useComplianceStatus = () => {
  const [data, setData] = useState<{
    overallCompliance: number;
    compliantMetrics: number;
    warningMetrics: number;
    nonCompliantMetrics: number;
    lastUpdated: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const status = await LaboratoryApiService.getComplianceStatus();
      setData(status);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : "Failed to fetch compliance status";
      setError(errorMessage);
      console.error("Error fetching compliance status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    data,
    loading,
    error,
    refetch: fetchStatus,
  };
};

// Hook for fetching laboratory summary
export const useLaboratorySummary = () => {
  const [data, setData] = useState<{
    totalSpecimens: number;
    totalProcedures: number;
    averageTurnaroundTime: number;
    complianceScore: number;
    lastUpdated: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const summary = await LaboratoryApiService.getLaboratorySummary();
      setData(summary);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : "Failed to fetch laboratory summary";
      setError(errorMessage);
      console.error("Error fetching laboratory summary:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    data,
    loading,
    error,
    refetch: fetchSummary,
  };
};

// Hook for filtering metrics by category
export const useMetricsByCategory = (category: string) => {
  const [data, setData] = useState<LaboratoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const metrics = await LaboratoryApiService.getMetricsByCategory(category);
      setData(metrics);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : "Failed to fetch metrics by category";
      setError(errorMessage);
      console.error(`Error fetching metrics for category ${category}:`, err);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    if (category) {
      fetchMetrics();
    }
  }, [category, fetchMetrics]);

  return {
    data,
    loading,
    error,
    refetch: fetchMetrics,
  };
};

// Hook for data export
export const useDataExport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportData = useCallback(
    async (
      format: "csv" | "excel" | "pdf",
      data: LaboratoryData[],
      dateRange?: { startDate: string; endDate: string }
    ) => {
      try {
        setLoading(true);
        setError(null);

        // Use client-side export utilities
        const blob = exportDataUtil(format, data, dateRange);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        
        // Set appropriate file extension
        const extension = format === 'excel' ? 'xlsx' : format;
        link.download = `laboratory-data-${
          new Date().toISOString().split("T")[0]
        }.${extension}`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to export data";
        setError(errorMessage);
        console.error("Error exporting data:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    exportData,
    loading,
    error,
  };
};
